import { Model } from '@vuex-orm/core';
import MontageTeamAPI from '@/api/MontageTeamAPI';
import { MontageTeamCreatePayload, MontageTeamUpdatePayload } from '@/interfaces/IMontageTeam';
import Vehicle from '@/models/Vehicle';

/**
 * MontageTeam model for logistics
 * Represents a montage team with personnel info
 */
export default class MontageTeam extends Model {
    public id!: string;
    public name!: string;
    public lastName!: string;
    public driver!: boolean;
    public teamLeader!: boolean;
    public installer!: boolean;
    public vehicleId!: string;
    public vehicle!: Vehicle;
    public createdAt!: string;
    public updatedAt!: string;

    public static entity = 'montageTeams';

    public static fields() {
        return {
            id: this.attr(null),
            name: this.attr(null),
            lastName: this.attr(null),
            driver: this.attr(null),
            teamLeader: this.attr(null),
            installer: this.attr(null),
            vehicleId: this.attr(null),
            vehicle: this.belongsTo(Vehicle, 'vehicleId'),
            createdAt: this.attr(null),
            updatedAt: this.attr(null),
        };
    }

    /**
     * Fetch all montage teams from the API
     */
    public static async fetchAll(): Promise<MontageTeam[]> {
        try {
            const response = await MontageTeamAPI.getAll();
            const teams = response?.data || [];
            await this.create({ data: teams, insertOrUpdate: ['vehicles'] });
            return teams;
        } catch (e) {
            return Promise.reject(e);
        }
    }

    /**
     * Fetch a single montage team by ID
     */
    public static async fetchById(id: string): Promise<MontageTeam | null> {
        try {
            const response = await MontageTeamAPI.getById(id);
            const team = response?.data;
            if (team) {
                await MontageTeam.insertOrUpdate({ data: team });
            }
            return team;
        } catch (e) {
            return Promise.reject(e);
        }
    }

    /**
     * Create a new montage team
     */
    public static async createNew(payload: MontageTeamCreatePayload): Promise<MontageTeam> {
        try {
            const response = await MontageTeamAPI.create(payload);
            const team = response?.data;
            if (team) {
                await MontageTeam.insertOrUpdate({ data: team });
            }
            return team;
        } catch (e) {
            return Promise.reject(e);
        }
    }

    /**
     * Update an existing montage team
     */
    public static async updateExisting(id: string, payload: MontageTeamUpdatePayload): Promise<MontageTeam> {
        try {
            const response = await MontageTeamAPI.update(id, payload);
            const team = response?.data;
            if (team) {
                await MontageTeam.insertOrUpdate({ data: team });
            }
            return team;
        } catch (e) {
            return Promise.reject(e);
        }
    }

    /**
     * Delete a montage team
     */
    public static async deleteById(id: string): Promise<void> {
        try {
            await MontageTeamAPI.delete(id);
            await MontageTeam.delete(id);
        } catch (e) {
            return Promise.reject(e);
        }
    }
}
